import React, { useState, useEffect } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format as formatDate, parse as parseDate, isValid } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TextField from '@mui/material/TextField';

import UiFieldWrapper from './UiFieldWrapper';
import UiBaseField from './UiBaseField';
import { getFieldData } from './UiFieldHelpers';

type IPropsUiDateField = UiBaseField & {
    format?: string,
    keyboard?: boolean,
    min?: string | Date,
    max?: string | Date,
    onSelect?(newValue: string | null): void
}

// Convert moment format to date-fns format
const convertMomentFormatToDateFns = (momentFormat: string): string => {
    return momentFormat
        .replace(/YYYY/g, 'yyyy')
        .replace(/DD/g, 'dd')
        .replace(/D/g, 'd');
};

export const UiDateField = (props: IPropsUiDateField) => {
    const { error, name, value } = getFieldData(props);
    const momentFormat: string = props.format || "YYYY-MM-DD";
    const dateFnsFormat = convertMomentFormatToDateFns(momentFormat);

    const parseValue = (val: string | null | undefined): Date | null => {
        if (!val) return null;
        try {
            const parsed = parseDate(val, dateFnsFormat, new Date());
            return isValid(parsed) ? parsed : null;
        } catch {
            return null;
        }
    };

    const [dateState, setDate] = useState<Date | null>(parseValue(value));

    useEffect(() => {
        setDate(parseValue(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleDateChange = (date: Date | null) => {
        setDate(date);
        const formattedValue = date && isValid(date) ? formatDate(date, dateFnsFormat) : null;
        if (props.form) {
            props.form.setFieldValue(name, formattedValue);
            if (props.onSelect) {
                props.onSelect(formattedValue);
            }
        }
    };

    const minDate = props.min ? (typeof props.min === 'string' ? parseValue(props.min) ?? undefined : props.min) : undefined;
    const maxDate = props.max ? (typeof props.max === 'string' ? parseValue(props.max) ?? undefined : props.max) : undefined;

    return (
        <UiFieldWrapper name="ui-date-field" {...props}>
            <div className="ui-field__field">
                <DatePicker
                    value={dateState}
                    onChange={handleDateChange}
                    inputFormat={dateFnsFormat}
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={props.disabled}
                    components={{
                        OpenPickerIcon: CalendarTodayIcon,
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            id={props.id}
                            fullWidth
                            variant="outlined"
                            label={props.label}
                            error={error.hasError}
                            helperText={error.errorMessage}
                            InputLabelProps={{
                                ...params.InputLabelProps,
                                style: { pointerEvents: "auto" }
                            }}
                        />
                    )}
                />
            </div>
        </UiFieldWrapper>
    )
}
export default UiDateField
